import { type Context, createContext, useContext, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { type UseWhoAmIReturn, useWhoAmIPrivate } from '@/api';
import { setNavigate } from '@/api/navigation';
import { setSessionExpiredHandler } from '@/api/session';
import { Loading } from '@/components';

const WhoAmIContext = createContext(null as unknown as UseWhoAmIReturn);

/**
 * Fires the initial authenticate request for all App routes and provides auth
 * state via context in useWhoAmI.
 *
 * Also registers React Router's navigate function so it can be used outside the
 * component tree (e.g. in Axios interceptors).
 *
 * Must be rendered inside the Router context.
 */
export function WhoAmI() {
  const navigate = useNavigate();
  const value = useWhoAmIPrivate();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  const { endSession } = value;
  useEffect(() => {
    setSessionExpiredHandler(endSession);
    return () => setSessionExpiredHandler(null);
  }, [endSession]);

  return (
    <WhoAmIContext.Provider value={value}>
      {value.isLoading ? <Loading /> : <Outlet />}
    </WhoAmIContext.Provider>
  );
}

export const useWhoAmI = () => {
  const context = useContext<UseWhoAmIReturn>(
    WhoAmIContext as unknown as Context<UseWhoAmIReturn>,
  );
  if (context === null) {
    throw new Error('useWhoAmI must be used within a WhoAmIProvider');
  }
  return context;
};
